export interface LearningResourceSubsection {
  title: string;
  /** Paragraphs joined with blank lines; rendered as separate `<p>` elements. */
  description: string;
}

export interface LearningResourceSection {
  id: string;
  heading: string;
  sectionIntro: string;
  subsections: LearningResourceSubsection[];
  /** YouTube video id for the embed (one tutorial per section). */
  youtubeId: string;
}

export const LEARNING_RESOURCE_SECTIONS: LearningResourceSection[] = [
  {
    id: 'getting-started-purchasing',
    heading: 'Getting Started and Purchasing Piph Author',
    sectionIntro:
      'Learn about what Piph can do and how to purchase unlimited access after the free trial.',
    youtubeId: '4A1gAbWoKTE',
    subsections: [
      {
        title: 'What Piph does',
        description: `Welcome to Piph Author where you can create bond-line drawings of molecules and reaction schemes and automatically generate the alt text of each image. Structures can be drawn using Alchemie's Sketcher drawing tool or existing structures can be imported from ChemDraw.`,
      },
      {
        title: 'Purchasing',
        description: `To get started, create a new account and verify your email address before signing in. New accounts start with a free trial before payment is required. For free you get 20 uses which can be any combination of generated descriptions and downloaded images.

After 20 free uses, you will be prompted to upgrade to an unlimited annual subscription. The subscription fee provides unlimited image downloads and alt text generation for a full calendar year from the date of purchase.

If you have an access code from your institution, enter it on the account screen to activate your subscription.`,
      },
      {
        title: 'Tutorial videos',
        description: `Reference the other tutorial videos for additional information on using Piph and all of its features, drawing with Sketcher, importing from ChemDraw, and exporting images with alt text.`,
      },
    ],
  },
  {
    id: 'navigating-features',
    heading: "Navigating Piph's Features",
    sectionIntro:
      'Learn how to create images of molecules and reaction schemes with Piph Author and all of the available features.',
    youtubeId: 'RKCIhvDqEtk',
    subsections: [
      {
        title: 'Creating images with Piph',
        description: `Let's discuss the process of creating images of reaction schemes with the reaction symbology or stand alone molecules, and all of the features that are available to do so.

First, select the desired Template, either Reaction Schemes or Molecules. The Molecules Template allows you to have one or more molecules in an image. Everything you can do with the Molecules Template, you can do with the Reaction Schemes Template, plus the added symbology so we'll start with creating a Reaction Scheme. Click on the Edit Reactant box to edit. Options for editing include drawing the structure, adding in text, copying the structure from the products, or pasting in a structure from ChemDraw. For further directions on how to draw with Sketcher, or import from ChemDraw, reference that specific tutorial video.

When a structure is present in either the reactant or product box, it can be copied to the other using the copy button in the other box. So here I can copy the structure from the reactants to the products. This allows for editing the given structure opposed to drawing from scratch.

It is also possible to not include a structure. The box can be left blank for predict the product questions, or text can be added.

To edit the arrow, first select it. Text can be added above and below the arrow. The text box is designed to allow for easy input of chemical formulas so adding a number after a letter formats it as a subscript, numbers before letters automatically appear as coefficients, and superscripts and additional symbols can be added with the buttons provided above. You can also choose the arrow type by selecting from the dropdown menu.`,
      },
      {
        title: 'Adjusting size and zoom',
        description: `When it comes to adjusting the positions of the reactant or product, simply click and drag the structures on either side of the arrow to move them. Additionally, the width and height of the image can be adjusted if desired, as well as the zoom. For incremental changes to the zoom, hover your mouse over the percent symbol and click and drag up or down to zoom in or out.`,
      },
      {
        title: 'Alt text description style',
        description: `To generate the alt text, choose the description style that best matches the image, then click generate. The provided alt text can be edited if desired by clicking and typing in the box.`,
      },
      {
        title: 'Export',
        description: `After adjusting all of the settings, export the image as either an SVG or PNG. For more information on the alt text and exporting images, reference the generating and exporting alt text tutorial video.`,
      },
      {
        title: 'Reset',
        description: `To start again and create another image, simply click reset.`,
      },
      {
        title: 'Upload',
        description: `Downloaded images from Piph can be reuploaded to access again and edit if needed. Click on Open under File and search for the desired image. Once uploaded, the image and generated alt text will populate, complete with any edits you may have made to the alt text. Edits, whether to the image or alt text, or both, can be made before exporting again.`,
      },
    ],
  },
  {
    id: 'sketcher',
    heading: 'How to Draw Structures with Sketcher',
    sectionIntro:
      "Learn how to use Alchemie's molecular drawing tool, Sketcher, which allows for creation of bond-line drawings.",
    youtubeId: 'DQDsZsnur8Q',
    subsections: [
      {
        title: 'Adding Atoms',
        description: `To start any structure, first add an atom or a molecule template to the workspace. There are two methods: either, click and drag the desired atom or molecule from the sidebar and drop it in the workspace; or click on an element or molecule in the atom bar to select it then click to add it to the workspace. Note, when an atom is selected in the atom bar you will see a watermark in the upper right corner that says "Place selected atom". This is called the atom stamp.`,
      },
      {
        title: 'Forming Bonds',
        description: `To form a bond, click and drag from one atom to another. Repeat to form double and triple bonds. The bond type can also be changed by clicking on the bond to select it, then clicking on the desired bond type in the bond menu on the left side of the workspace. Notice how this menu only appears when it is applicable, that is, only when a bond is selected. Alternatively, there are keyboard shortcuts to change the bond type when a bond is selected. 1, 2, and 3 change the bond to a single, double, or triple bond, respectively. Or w, d, and s, can be used to change the bond to a wedge, dash, or squiggle. The direction of a bond can be flipped by clicking the arrow button at the bottom of the bond menu or by pressing f. Click in empty space to deselect the bond.

Bonds can also be formed by dragging from an existing atom in the workspace. If a particular atom is not selected, the bond defaults to carbon. Otherwise, click on an atom in the atom bar to turn on that atom stamp – notice the watermark in the corner. Dragging from an existing atom will add the selected atom to the end of a bond. The same can be done with molecule templates. Click on the atom in the atom bar to turn off the atom stamp.

When forming a long carbon chain, hold down shift and click and drag from an existing carbon to add a chain of a desired length.`,
      },
      {
        title: 'Replacing Atoms',
        description: `When editing a structure, there are 4 ways to replace atoms: (1) Select an atom in the workspace and click another element in the atom bar. (2) Select an element in the atom bar and click on an atom in the workspace to replace. Click the element in the atom bar to turn off the atom stamp. (3) Drag and drop an atom onto another. (4) Select an atom in the workspace and type the first letter of the atomic symbol. Press this letter again to cycle through all elements that begin with that letter.`,
      },
      {
        title: 'Selecting and Moving Atoms & Molecules',
        description: `Notice how the default mode in Sketcher is the selection mode, meaning as long as the atom stamp is not on, clicking on an item in the workspace selects it, as indicated by the blue highlight. Once an atom is selected, it can be moved by clicking and dragging. Click in empty space to deselect the object.

An entire molecule can be selected by double clicking on an atom. Clicking and dragging an atom or bond will move the molecule. Dragging a box around the molecule can select all or a portion of it. Similarly, a selected portion can be moved and will move about the attached bond. Multiple atoms and bonds can be selected by holding down shift while clicking on the object. This is helpful for performing actions on multiple atoms or bonds at once.`,
      },
      {
        title: 'Rotating Molecules',
        description: `When an entire molecule is selected, use the rotate molecule button in the menu on the left to rotate the molecule. Or, select a portion of a molecule and use the rotate button to rotate that part of the molecule about the connection point to the rest of the molecule.`,
      },
      {
        title: 'Show or Hide Implied Hydrogens or Lone Pairs',
        description: `When an atom is selected, the atom menu on the left appears. The top two buttons can be used to show or hide lone pairs and implied hydrogens. All lone pairs are automatically included. Implied hydrogens on carbon are included and can be shown as well. On a heteroatom, the hydrogens can be shown to see the bonds or collapsed.`,
      },
      {
        title: 'Adding Charges',
        description: `When an atom is selected, click the "plus charge" or "minus charge" buttons to add a charge. The number of hydrogen atoms and lone pairs on an atom will automatically change accordingly. Editing the atom will remove the charge label. Note, charges are not automatically calculated. The user always has to add the charge label. Click and drag labels to adjust their location around the atom.`,
      },
      {
        title: 'Adding Radicals',
        description: `When the option is available in certain questions, radicals can be added by selecting an atom and clicking on the radical button. Adding a radical will adjust the atom by removing a hydrogen or lone pair.`,
      },
      {
        title: 'Deleting Atoms and Bonds',
        description: `There are three methods for deleting atoms and bonds: (1) select an atom or bond and click the "erase" button. (2) Select an atom or bond and press the "delete" key on the keyboard. (3) Select the "erase" button in the toolbar and click on the atoms and bonds you want to delete. When the erase button is selected, a watermark will appear in the upper right corner that says "erase". Click the "erase" button again to turn it off.

The reset button will reset the entire workspace to its original state, whether it was blank or had a structure to begin with.`,
      },
      {
        title: 'Deleting vs Hiding Hydrogens',
        description: `It is important to note that deleting a hydrogen atom is different from hiding a hydrogen atom. In Sketcher, hydrogens on carbon cannot be deleted. Rather, they can be hidden, and thus implied. However, if a carbon ends up with more than 4 bonds, hydrogens can be deleted in order to return to a valid structure. When in an invalid state, the hydrogens on carbon are not able to be hidden to avoid ambiguity regarding the number of implied hydrogens.`,
      },
      {
        title: 'Fusing Rings',
        description: `To fuse rings, add the two rings to the workspace. If needed, rotate one of the rings so that the bond to be shared on each ring is roughly parallel to each other. Select one ring and drag until the shared atoms overlap then let go. Double check that the rings successfully fused by double clicking to see if the whole molecule is selected.`,
      },
      {
        title: 'Clean Up Structure Button',
        description: `When drawing structures, bond lengths and angles tend to snap into relatively correct positions, however, the layout can be fixed by selecting the entire molecule and clicking the "clean up" button in the menu on the left.`,
      },
      {
        title: 'Copy and Paste',
        description: `Lastly, to copy and paste or duplicate, select a structure then use Ctrl + C and Ctrl + V or the duplicate button on the left.`,
      },
      {
        title: 'Info Panel',
        description: `All of these directions can be referenced at any time by clicking on the i button in the top left corner of the tool.`,
      },
    ],
  },
  {
    id: 'chemdraw-import',
    heading: 'Inputting ChemDraw Structures into Piph',
    sectionIntro:
      'Learn how to copy structures from ChemDraw into Piph Author and generate alt text for existing course material.',
    youtubeId: 'ggqgSbTB6yY',
    subsections: [
      {
        title: 'Copying from ChemDraw',
        description: `Open the ChemDraw file containing the reaction or molecule you want to bring into Piph. You can copy one or more structures at a time, but each side of a reaction scheme — reactants and products — needs to be copied separately.

Select the structure or structures you want to copy. Then go to Edit, Copy As, and choose CDXML.`,
      },
      {
        title: 'Pasting into Piph Author',
        description: `Switch over to Piph Author and click on the area where you want to paste — for example, click Edit Reactant. Then paste using Command-V on Mac or Ctrl-V on PC. The structure will appear directly.

For reaction schemes, repeat this process for the products. Go back to ChemDraw, select the product structure or structures, copy as CDXML, then return to Piph Author, click Edit Product, and paste.`,
      },
      {
        title: 'Reaction conditions',
        description: `Reaction conditions above and below the arrow can also be copied from ChemDraw and pasted into the corresponding text fields in Piph Author. For these, just use a standard copy and paste — you do not need to use Copy As CDXML since it is text, not a structure.

Note that the text above the arrow and below the arrow are separate fields, so each needs to be copied and pasted individually.`,
      },
    ],
  },
  {
    id: 'alt-text-export',
    heading: 'Generating and Exporting Alt Text with Diagrams',
    sectionIntro:
      'Learn how to generate and export alt text with images in Piph Author and embed them in course content.',
    youtubeId: '-K4lfkpdGrQ',
    subsections: [
      {
        title: 'Generating and editing alt text',
        description: `After creating the image in Piph, the alt text can be generated. First choose the description style to provide more context for the alt text. After clicking generate, the description will populate in the box.

If desired, the alt text can be edited by clicking and typing in the box. This can be used to include particular phrasing or provide more or less detail based on the context of the image. The changes to the descriptions will be saved automatically when exported.`,
      },
      {
        title: 'Structure Tree',
        description: `The Structure Tree panel gives you a navigable breakdown of the molecule's structure. It's organized hierarchically: molecule summary at the top, then each primary feature such as chains, rings, and functional groups, and you can expand any feature to see individual atoms, bonds, and substituents.

This can be used to provide more specific detail that may be important in the context of the image, such as a particular carbocation that is bonded to three carbon atoms and is therefore tertiary, or that a leaving group is on a specific carbon. The Structure Tree lets you find that detail and add it to the alt text to supplement the automatically generated description.`,
      },
      {
        title: 'PNG vs. SVG — Which Should You Use?',
        description: `To export the image, enter a file name and choose the desired format, either a PNG or SVG. What is the difference between PNG and SVG? PNGs are common images that are widely accepted in different applications.

SVGs are not always supported in various applications but they provide additional information and allow screen reader users to navigate the image to receive deeper alt text descriptions.

Which file format you choose depends on the application of the image.`,
      },
      {
        title: 'Alt text is saved with the image',
        description: `When exporting the image, the alt text is included in the metadata. If importing the image into an application suited for image processing, the alt text is automatically included. But when putting an image into a word document, for example, you will need to copy the alt text from Piph Author and paste it into the image's alt text box in the document.`,
      },
    ],
  },
];
